import { useEffect } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";

import { toErrorMessage } from "@src/shared/utils/errors";
import { events } from "@src/shared/utils/observability/events";

import { ApplicationErrorPageV2 } from "../../../ui/pages/v2/ApplicationErrorPage";
import { NotFoundPageV2 } from "../../../ui/pages/v2/NotFoundPage";

/**
 * Root error boundary for the v2 route tree.
 *
 * Per React Router v6 best practices, when no route matches a URL React Router
 * automatically throws a 404 `Response` that bubbles up to the nearest
 * `errorElement` rather than relying on a splat (`*`) route. This boundary
 * routes a thrown 404 to the v2 not-found page and falls back to the v2
 * application-error page for any other error.
 *
 * It replaces the root route's element, so `RootV2Route` never renders and the
 * `.theme-v2` wrapper has to be applied here for the v2 design tokens to apply.
 *
 * @see https://reactrouter.com/6.30.3/route/error-element
 */
export function ErrorBoundaryV2() {
  const error = useRouteError();
  const is404 = isRouteErrorResponse(error) && error.status === 404;

  if (!is404) {
    console.error("[ErrorBoundaryV2] Unhandled route error:", error);
  }

  useEffect(() => {
    if (is404) return;

    const errorCode = isRouteErrorResponse(error)
      ? String(error.status)
      : undefined;

    const errorMessage = isRouteErrorResponse(error)
      ? `${error.status} ${error.statusText}`
      : toErrorMessage(error);

    events.appError.track({
      surface: "application",
      error: errorMessage,
      ...(errorCode !== undefined && { error_code: errorCode }),
      fatal: true,
    });
  }, [error, is404]);

  return (
    <main className="theme-v2 flex min-h-dvh flex-col items-center justify-center bg-background text-foreground font-sans">
      {is404 ? <NotFoundPageV2 /> : <ApplicationErrorPageV2 />}
    </main>
  );
}
